/**
 * BallEditModal — edit a single ball (runs / overthrow / extras / wicket).
 * On save, backend replays the entire innings so all downstream state stays consistent.
 */
import { useState } from 'react';

const WICKET_TYPES_FOR_EDIT = [
  { value: 'bowled', label: 'Bowled' },
  { value: 'caught', label: 'Caught' },
  { value: 'lbw', label: 'LBW' },
  { value: 'run_out', label: 'Run Out' },
  { value: 'stumped', label: 'Stumped' },
  { value: 'hit_wicket', label: 'Hit Wicket' },
  { value: 'retired_hurt', label: 'Retired Hurt' },
  { value: 'retired_out', label: 'Retired Out' },
];

const EXTRAS_FOR_EDIT = [
  { value: '', label: 'None' },
  { value: 'wide', label: 'Wide' },
  { value: 'no_ball', label: 'No-Ball' },
  { value: 'bye', label: 'Bye' },
  { value: 'leg_bye', label: 'Leg-Bye' },
];

export default function BallEditModal({ ball, onSubmit, onDelete, onCancel, loading }) {
  const [state, setState] = useState({
    runs_scored: ball.runs_scored || 0,
    overthrow_runs: ball.overthrow_runs || 0,
    extras_type: ball.extras_type || '',
    extras_runs: ball.extras_runs || 0,
    is_wicket: !!ball.is_wicket,
    wicket_type: ball.wicket_type || 'bowled',
    is_boundary_four: !!ball.is_boundary_four,
    is_boundary_six: !!ball.is_boundary_six,
    commentary: ball.commentary || '',
  });

  const extrasType = state.extras_type || null;
  const allowedWicketTypes = extrasType === 'wide'
    ? WICKET_TYPES_FOR_EDIT.filter(w => ['stumped', 'run_out'].includes(w.value))
    : extrasType === 'no_ball'
      ? WICKET_TYPES_FOR_EDIT.filter(w => w.value === 'run_out')
      : WICKET_TYPES_FOR_EDIT;

  const handleSave = () => {
    const overrides = {
      runs_scored: state.runs_scored,
      overthrow_runs: state.overthrow_runs,
      extras_type: state.extras_type || null,
      extras_runs: state.extras_runs,
      is_wicket: state.is_wicket,
      wicket_type: state.is_wicket ? state.wicket_type : null,
      is_boundary_four: state.runs_scored === 4,
      is_boundary_six: state.runs_scored === 6,
      commentary: state.commentary,
    };
    onSubmit(overrides);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100]"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="ball-edit-modal"
      >
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-bold text-lg text-[#2B3A67]">Edit Ball</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1"
            aria-label="Close"
            data-testid="ball-edit-close"
          >×</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Over {ball.over_number}.{ball.ball_number} — editing will recompute the entire innings.
        </p>

        {/* Runs */}
        <div className="mb-3">
          <label className="text-xs text-gray-500 block mb-2">Runs</label>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4, 6].map(r => (
              <button
                key={r}
                onClick={() => setState(s => ({ ...s, runs_scored: r }))}
                disabled={extrasType === 'wide'}
                className={`flex-1 py-2 rounded-lg font-bold text-sm ${
                  state.runs_scored === r
                    ? r === 4 ? 'bg-blue-500 text-white'
                    : r === 6 ? 'bg-purple-500 text-white'
                    : 'bg-[#3AAFA9] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
                }`}
                data-testid={`edit-runs-${r}`}
              >{r}</button>
            ))}
          </div>
        </div>

        {/* Overthrows */}
        <div className="mb-3">
          <label className="text-xs text-gray-500 block mb-2">Overthrows</label>
          <div className="flex gap-1.5">
            {[0, 1, 2, 3, 4].map(v => (
              <button
                key={v}
                onClick={() => setState(s => ({ ...s, overthrow_runs: v }))}
                className={`flex-1 py-1.5 rounded-lg text-sm font-medium ${
                  state.overthrow_runs === v ? 'bg-amber-500 text-white' : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                data-testid={`edit-overthrow-${v}`}
              >+{v}</button>
            ))}
          </div>
        </div>

        {/* Extras */}
        <div className="mb-3">
          <label className="text-xs text-gray-500 block mb-1">Extras</label>
          <select
            value={state.extras_type || ''}
            onChange={e => setState(s => ({ ...s, extras_type: e.target.value, extras_runs: 0 }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            data-testid="edit-extras-type"
          >
            {EXTRAS_FOR_EDIT.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          {extrasType && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-gray-500">Extras runs:</label>
              <input
                type="number" min={0} max={6}
                value={state.extras_runs}
                onChange={e => setState(s => ({ ...s, extras_runs: parseInt(e.target.value) || 0 }))}
                className="w-16 border border-gray-200 rounded px-2 py-1 text-sm"
                data-testid="edit-extras-runs"
              />
            </div>
          )}
        </div>

        {/* Wicket */}
        <div className="mb-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={state.is_wicket}
              onChange={e => setState(s => ({ ...s, is_wicket: e.target.checked }))}
              data-testid="edit-is-wicket"
            />
            Wicket
          </label>
          {state.is_wicket && (
            <select
              value={state.wicket_type}
              onChange={e => setState(s => ({ ...s, wicket_type: e.target.value }))}
              className="w-full mt-2 border border-gray-200 rounded-lg px-3 py-2 text-sm"
              data-testid="edit-wicket-type"
            >
              {allowedWicketTypes.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          )}
        </div>

        {/* Commentary */}
        <div className="mb-4">
          <label className="text-xs text-gray-500 block mb-1">Commentary</label>
          <input
            type="text"
            value={state.commentary}
            onChange={e => setState(s => ({ ...s, commentary: e.target.value }))}
            placeholder="Optional commentary"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
            data-testid="edit-commentary"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
            data-testid="edit-cancel-btn"
          >Cancel</button>
          {onDelete && (
            <button
              onClick={() => {
                if (window.confirm(`Delete ball ${ball.over_number}.${ball.ball_number}? This cannot be undone — the innings will be recomputed.`)) {
                  onDelete();
                }
              }}
              disabled={loading}
              className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 font-bold rounded-lg hover:bg-red-100 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="edit-delete-btn"
              title="Delete this ball (Scorer / Admin / Super Admin)"
            ><i className="ph-bold ph-trash"></i>Delete</button>
          )}
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 py-2 bg-[#3AAFA9] text-white font-bold rounded-lg hover:bg-[#2D8A85] disabled:opacity-50"
            data-testid="edit-save-btn"
          >{loading ? 'Saving...' : 'Save & Recompute'}</button>
        </div>
      </div>
    </div>
  );
}
